/**
 * Site navigation: mega menu, mobile menu and sticky header.
 */

const OPEN_CLASS = 'is-open';

/**
 * Desktop mega menu.
 *
 * Opens on hover for pointer users and on click/Enter for keyboard and touch,
 * so the panel is reachable without a hover state.
 *
 * @param {HTMLElement} header Site header element.
 */
function initMegaMenu( header ) {
	const items = Array.from( header.querySelectorAll( '[data-mega-item]' ) );

	if ( ! items.length ) {
		return;
	}

	// A panel video sits in a display:none container, where browsers decline to
	// start playback, so the autoplay attribute alone never fires. Drive it from
	// the open/close state instead. play() rejects if the browser blocks it —
	// nothing to recover, but the rejection has to be swallowed.
	const setVideoPlaying = ( item, isPlaying ) => {
		const video = item.querySelector( '[data-mega-video]' );

		if ( ! video ) {
			return;
		}

		if ( ! isPlaying ) {
			video.pause();
			return;
		}

		video.play()?.catch( () => {} );
	};

	const closeAll = ( except ) => {
		items.forEach( ( item ) => {
			if ( item === except ) {
				return;
			}

			const panel = item.querySelector( '[data-mega-panel]' );
			const toggle = item.querySelector( '[data-mega-toggle]' );

			item.classList.remove( OPEN_CLASS );
			panel?.classList.add( 'hidden' );
			toggle?.setAttribute( 'aria-expanded', 'false' );
			setVideoPlaying( item, false );
		} );

		if ( ! except ) {
			header.classList.remove( 'is-mega-open' );
		}
	};

	const open = ( item ) => {
		const panel = item.querySelector( '[data-mega-panel]' );
		const toggle = item.querySelector( '[data-mega-toggle]' );

		closeAll( item );
		item.classList.add( OPEN_CLASS );
		panel?.classList.remove( 'hidden' );
		toggle?.setAttribute( 'aria-expanded', 'true' );
		header.classList.add( 'is-mega-open' );
		setVideoPlaying( item, true );
	};

	// Small grace period so a diagonal move from the trigger to the panel — or
	// a slip across the gap between two triggers — doesn't close the menu.
	let closeTimer = null;

	const cancelClose = () => {
		if ( closeTimer ) {
			clearTimeout( closeTimer );
			closeTimer = null;
		}
	};

	const closeSoon = () => {
		cancelClose();
		closeTimer = setTimeout( () => closeAll(), 220 );
	};

	items.forEach( ( item ) => {
		const toggle = item.querySelector( '[data-mega-toggle]' );

		item.addEventListener( 'mouseenter', () => {
			cancelClose();
			open( item );
		} );

		item.addEventListener( 'mouseleave', closeSoon );

		toggle?.addEventListener( 'click', ( event ) => {
			event.preventDefault();
			cancelClose();

			if ( item.classList.contains( OPEN_CLASS ) ) {
				closeAll();
			} else {
				open( item );
			}
		} );

		item.addEventListener( 'focusout', ( event ) => {
			if ( ! item.contains( event.relatedTarget ) ) {
				closeAll();
			}
		} );
	} );

	// Re-entering the header at all cancels a pending close.
	header.addEventListener( 'mouseenter', cancelClose );

	document.addEventListener( 'keydown', ( event ) => {
		if ( 'Escape' === event.key ) {
			closeAll();
		}
	} );

	document.addEventListener( 'click', ( event ) => {
		if ( ! header.contains( event.target ) ) {
			closeAll();
		}
	} );
}

/**
 * Off canvas mobile menu.
 */
function initMobileMenu() {
	const panel = document.querySelector( '[data-mobile-menu]' );
	const toggle = document.querySelector( '[data-menu-toggle]' );
	if ( ! panel || ! toggle ) {
		return;
	}

	const setState = ( isOpen ) => {
		panel.classList.toggle( 'hidden', ! isOpen );
		toggle.setAttribute( 'aria-expanded', isOpen ? 'true' : 'false' );
		document.body.classList.toggle( 'has-menu-open', isOpen );
	};

	toggle.addEventListener( 'click', () => {
		setState( panel.classList.contains( 'hidden' ) );
	} );

	document.querySelectorAll( '[data-menu-close]' ).forEach( ( el ) => {
		el.addEventListener( 'click', () => setState( false ) );
	} );

	document.addEventListener( 'keydown', ( event ) => {
		if ( 'Escape' === event.key ) {
			setState( false );
		}
	} );

	// Sub-menu expand/collapse is handled by the Disclosure component so the
	// animation matches the FAQ accordion.
}

/**
 * Sticky header: solid once the hero has scrolled away.
 *
 * @param {HTMLElement} header Site header element.
 */
function initStickyHeader( header ) {
	const threshold = 120;

	const update = () => {
		header.classList.toggle( 'is-stuck', window.scrollY > threshold );
	};

	update();
	window.addEventListener( 'scroll', update, { passive: true } );
}

export function Navigation() {
	const header = document.querySelector( '[data-site-header]' );

	if ( ! header ) {
		return;
	}

	initMegaMenu( header );
	initMobileMenu();
	initStickyHeader( header );
}
